import styles from 'css/src/components/public-pages/PageListing.module.scss';

import React from 'react';
import classNames from 'classnames';

import { SecondaryButton } from 'js/src/components/common/ui/Button';
import TextInput from 'js/src/components/common/ui/TextInput';
import ContentfulImageOrVideo from 'js/src/components/public-pages/ContentfulImageOrVideo';
import type { DeepReadonly } from 'js/src/libs/consts';
import type { ContentfulPageType } from 'js/src/libs/contentful-utils';

const ALL_CATEGORIES = 'All';
const UNCATEGORIZED = 'Other';

type ListedPage = {
    page: DeepReadonly<ContentfulPageType>;
    category: string;
    searchText: string;
};

type Props = {
    data: DeepReadonly<{
        pages: Array<ContentfulPageType>;
    }>;
    bgColor: string;
    lazyLoad: boolean;
};

const PageListing = React.memo(function PageListing({ data, bgColor, lazyLoad }: Props) {
    const [query, setQuery] = React.useState('');
    const [selectedCategory, setSelectedCategory] = React.useState(ALL_CATEGORIES);
    const searchInputId = React.useId();

    const listedPages: ListedPage[] = React.useMemo(
        () =>
            (data.pages ?? []).map((page) => {
                const category = page.listing_category || UNCATEGORIZED;
                return {
                    page,
                    category,
                    searchText: [page.link_text, page.description, category]
                        .join(' ')
                        .toLowerCase(),
                };
            }),
        [data.pages],
    );

    // Editors order the categories by ordering the pages in the listing. Uncategorized pages
    // always come last.
    const categories = React.useMemo(() => {
        const inListingOrder = [...new Set(listedPages.map(({ category }) => category))];
        return [
            ...inListingOrder.filter((category) => category !== UNCATEGORIZED),
            ...inListingOrder.filter((category) => category === UNCATEGORIZED),
        ];
    }, [listedPages]);
    const isCategorized = categories.some((category) => category !== UNCATEGORIZED);

    const normalizedQuery = query.trim().toLowerCase();
    const matchingQuery = React.useMemo(
        () =>
            normalizedQuery
                ? listedPages.filter(({ searchText }) => searchText.includes(normalizedQuery))
                : listedPages,
        [listedPages, normalizedQuery],
    );

    const handleQueryChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setQuery(event.target.value),
        [],
    );
    const clearQuery = React.useCallback(() => setQuery(''), []);

    if (!data.pages) {
        return null;
    }

    const countFor = (category: string) =>
        category === ALL_CATEGORIES
            ? matchingQuery.length
            : matchingQuery.filter((listedPage) => listedPage.category === category).length;

    const visiblePages =
        selectedCategory === ALL_CATEGORIES
            ? matchingQuery
            : matchingQuery.filter(({ category }) => category === selectedCategory);

    const grid = (
        <ul className={styles['grid']}>
            {visiblePages.map(({ page, category }) => (
                <li key={page.slug}>
                    <PageCard
                        page={page}
                        category={isCategorized ? category : undefined}
                        lazyLoad={lazyLoad}
                    />
                </li>
            ))}
        </ul>
    );

    return (
        <div className="contentful-component" style={{ backgroundColor: bgColor }}>
            <div className={classNames('container', { [styles['layout']]: isCategorized })}>
                {isCategorized ? (
                    <>
                        <aside className={styles['sidebar']}>
                            <div className={styles['search']}>
                                <TextInput
                                    id={searchInputId}
                                    labelText={<span className="visually-hidden">Search</span>}
                                    text={query}
                                    iconLeft="search"
                                    placeholder="Search"
                                    onChange={handleQueryChange}
                                    showClear
                                    onClear={clearQuery}
                                />
                            </div>
                            <nav aria-label="Categories">
                                <ul className={styles['category-list']}>
                                    {[ALL_CATEGORIES, ...categories].map((category) => {
                                        const isSelected = category === selectedCategory;
                                        return (
                                            <li key={category}>
                                                <button
                                                    type="button"
                                                    className={classNames(
                                                        styles['category-button'],
                                                        {
                                                            [styles['category-button--selected']]:
                                                                isSelected,
                                                        },
                                                    )}
                                                    aria-pressed={isSelected}
                                                    onClick={(event) => {
                                                        setSelectedCategory(category);
                                                        // On small screens the categories scroll sideways.
                                                        event.currentTarget.scrollIntoView?.({
                                                            block: 'nearest',
                                                            inline: 'nearest',
                                                            behavior: 'smooth',
                                                        });
                                                    }}
                                                >
                                                    <span>{category}</span>
                                                    <span className={styles['category-count']}>
                                                        {countFor(category)}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        </aside>

                        <section
                            className={styles['results']}
                            aria-labelledby={`${searchInputId}-heading`}
                        >
                            <div className={styles['results-header']}>
                                <h2
                                    id={`${searchInputId}-heading`}
                                    className={styles['results-title']}
                                >
                                    {selectedCategory}
                                </h2>
                                <span className={styles['results-count']} aria-live="polite">
                                    {visiblePages.length === 1
                                        ? '1 result'
                                        : `${visiblePages.length} results`}
                                </span>
                            </div>

                            {visiblePages.length > 0 ? (
                                grid
                            ) : (
                                <div className={styles['empty-state']}>
                                    <span
                                        translate="no"
                                        className={`material-symbols-outlined ${styles['empty-state-icon']}`}
                                        aria-hidden="true"
                                    >
                                        search_off
                                    </span>
                                    <p className="font-l5 font-l5--regular--semibold mb-l5-4">
                                        Nothing matches “{query.trim()}”
                                    </p>
                                    <p className="font-l5 font-l5--small mb-l5-16">
                                        Try another name, or browse everything.
                                    </p>
                                    <SecondaryButton
                                        text="Show everything"
                                        onClick={() => {
                                            setQuery('');
                                            setSelectedCategory(ALL_CATEGORIES);
                                        }}
                                    />
                                </div>
                            )}
                        </section>
                    </>
                ) : (
                    grid
                )}
            </div>
        </div>
    );
});

type PageCardProps = {
    page: DeepReadonly<ContentfulPageType>;
    category?: string;
    lazyLoad: boolean;
};

const PageCard: React.FC<PageCardProps> = ({ page, category, lazyLoad }) => (
    <a className={styles['card']} href={`/${page.slug}`}>
        <div className={styles['card-image']}>
            <ContentfulImageOrVideo data={page.image} urlWidth={600} lazyLoad={lazyLoad} />
        </div>
        <div className={styles['card-body']}>
            {category && <span className={styles['card-category']}>{category}</span>}
            <h3 className={styles['card-title']}>{page.link_text}</h3>
            <p className={styles['card-description']}>{page.description}</p>
            <span className={styles['card-cta']} aria-hidden="true">
                Learn more
                <span
                    translate="no"
                    className={`material-symbols-outlined ${styles['card-cta-icon']}`}
                >
                    arrow_forward
                </span>
            </span>
        </div>
    </a>
);

export default PageListing;
